import React, { useState, useEffect } from 'react'
import {
  FileText,
  Download,
  Calculator,
  Plus,
  Trash2,
  Info,
  CheckSquare,
  AlertTriangle,
  Copy
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useToastHelpers } from '../../contexts/ToastContext'
import {
  ScheduleEData,
  ScheduleEFormState,
  SCHEDULE_E_EXPENSE_CATEGORIES,
  OtherExpense
} from '../../types/tax-analysis'
import { PropertyResponse } from '../../services/api'

interface ScheduleEHelperProps {
  property: PropertyResponse
  selectedYear?: string
  onDataChange?: (data: ScheduleEData) => void
  loading?: boolean
}

const EXPENSE_CATEGORY_LABELS = {
  advertising: 'Advertising',
  auto_travel: 'Auto and Travel',
  cleaning_maintenance: 'Cleaning and Maintenance',
  commissions: 'Commissions',
  insurance: 'Insurance',
  legal_professional: 'Legal and Professional Services',
  management_fees: 'Management Fees',
  mortgage_interest: 'Mortgage Interest Paid to Banks',
  other_interest: 'Other Interest',
  repairs: 'Repairs',
  supplies: 'Supplies',
  taxes: 'Taxes',
  utilities: 'Utilities',
  depreciation: 'Depreciation Expense'
} as const

const SCHEDULE_E_TIPS = {
  advertising: 'Costs for advertising rental property (online listings, signs, newspapers)',
  auto_travel: 'Vehicle expenses and travel costs for rental property management',
  cleaning_maintenance: 'Cleaning services and general maintenance costs',
  commissions: 'Real estate agent commissions for finding tenants',
  insurance: 'Property insurance premiums (do not include mortgage insurance)',
  legal_professional: 'Attorney fees, accounting fees, property management fees',
  management_fees: 'Fees paid to property management companies',
  mortgage_interest: 'Interest portion of mortgage payments (not principal)',
  other_interest: 'Interest on loans for property improvements or repairs',
  repairs: 'Costs to fix or maintain property (not improvements)',
  supplies: 'Materials and supplies for property maintenance',
  taxes: 'Real estate taxes and other property-related taxes',
  utilities: 'Utilities paid by landlord (electricity, gas, water, etc.)',
  depreciation: 'Calculated depreciation expense for the rental property'
}

const CURRENT_YEAR = new Date().getFullYear()

export function ScheduleEHelper({
  property,
  selectedYear = CURRENT_YEAR.toString(),
  onDataChange,
  loading = false
}: ScheduleEHelperProps) {
  const { showSuccess, showError } = useToastHelpers()
  const [formState, setFormState] = useState<ScheduleEFormState>({
    property_id: property.property_id || 'temp',
    year: selectedYear,
    rental_income: '',
    expenses: {},
    other_expenses: [],
    errors: {},
    isSubmitting: false
  })

  const [scheduleEData, setScheduleEData] = useState<ScheduleEData | null>(null)

  // Initialize form with property data
  useEffect(() => {
    const initialExpenses: Record<string, string> = {}

    // Pre-populate with property data where available
    initialExpenses.insurance = property.annual_insurance?.toString() || ''
    initialExpenses.taxes = property.annual_taxes?.toString() || ''
    initialExpenses.mortgage_interest = calculateMortgageInterest().toString()

    setFormState(prev => ({
      ...prev,
      property_id: property.property_id || 'temp',
      year: selectedYear,
      rental_income: ((property.expected_monthly_rent || 0) * 12).toString(),
      expenses: initialExpenses
    }))
  }, [property, selectedYear])

  // Calculate Schedule E data when form changes
  useEffect(() => {
    const data = calculateScheduleEData()
    setScheduleEData(data)
    if (onDataChange) {
      onDataChange(data)
    }
  }, [formState])

  const calculateMortgageInterest = (): number => {
    if (!property.loan_amount || !property.interest_rate || !property.loan_term) {
      return 0
    }

    const monthlyPayment = calculateMonthlyPayment(
      property.loan_amount,
      property.interest_rate,
      property.loan_term
    )

    // Approximate annual interest (rough calculation for first year)
    return monthlyPayment * 12 * 0.8 // Assume ~80% is interest in early years
  }

  const calculateMonthlyPayment = (loanAmount: number, annualRate: number, termYears: number): number => {
    const monthlyRate = annualRate / 100 / 12
    const numPayments = termYears * 12

    if (monthlyRate === 0) {
      return loanAmount / numPayments
    }

    return loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
           (Math.pow(1 + monthlyRate, numPayments) - 1)
  }

  const calculateScheduleEData = (): ScheduleEData => {
    const rentalIncome = parseFloat(formState.rental_income) || 0

    const expenseAmounts: Record<string, number> = {}
    let totalExpenses = 0

    // Calculate regular expenses
    SCHEDULE_E_EXPENSE_CATEGORIES.forEach(category => {
      const amount = parseFloat(formState.expenses[category] || '0') || 0
      expenseAmounts[category] = amount
      totalExpenses += amount
    })

    // Add other expenses
    const otherExpensesTotal = formState.other_expenses.reduce((sum, expense) => sum + expense.amount, 0)
    totalExpenses += otherExpensesTotal

    const netRentalIncome = rentalIncome - totalExpenses

    return {
      property_id: formState.property_id,
      property_address: property.property_address,
      year: formState.year,
      rental_income: rentalIncome,
      advertising: expenseAmounts.advertising || 0,
      auto_travel: expenseAmounts.auto_travel || 0,
      cleaning_maintenance: expenseAmounts.cleaning_maintenance || 0,
      commissions: expenseAmounts.commissions || 0,
      insurance: expenseAmounts.insurance || 0,
      legal_professional: expenseAmounts.legal_professional || 0,
      management_fees: expenseAmounts.management_fees || 0,
      mortgage_interest: expenseAmounts.mortgage_interest || 0,
      other_interest: expenseAmounts.other_interest || 0,
      repairs: expenseAmounts.repairs || 0,
      supplies: expenseAmounts.supplies || 0,
      taxes: expenseAmounts.taxes || 0,
      utilities: expenseAmounts.utilities || 0,
      depreciation: expenseAmounts.depreciation || 0,
      other_expenses: formState.other_expenses,
      total_expenses: totalExpenses,
      net_rental_income: netRentalIncome
    }
  }

  const updateExpense = (category: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      expenses: {
        ...prev.expenses,
        [category]: value
      },
      errors: {
        ...prev.errors,
        [category]: ''
      }
    }))
  }

  const addOtherExpense = () => {
    setFormState(prev => ({
      ...prev,
      other_expenses: [
        ...prev.other_expenses,
        { description: '', amount: 0, category: 'Other' }
      ]
    }))
  }

  const updateOtherExpense = (index: number, field: keyof OtherExpense, value: any) => {
    setFormState(prev => ({
      ...prev,
      other_expenses: prev.other_expenses.map((expense, i) =>
        i === index ? { ...expense, [field]: value } : expense
      )
    }))
  }

  const removeOtherExpense = (index: number) => {
    setFormState(prev => ({
      ...prev,
      other_expenses: prev.other_expenses.filter((_, i) => i !== index)
    }))
  }

  const handleExportScheduleE = (format: 'csv' | 'pdf' | 'turbotax') => {
    if (!scheduleEData) {
      showError('Export Error', 'No Schedule E data to export')
      return
    }

    try {
      let content: string
      let filename: string
      let mimeType: string

      switch (format) {
        case 'csv':
          content = generateScheduleECSV(scheduleEData)
          filename = `schedule-e-${property.property_address.replace(/\s+/g, '-')}-${selectedYear}.csv`
          mimeType = 'text/csv'
          break

        case 'pdf':
          // For now, generate HTML that can be printed to PDF
          content = generateScheduleEHTML(scheduleEData)
          filename = `schedule-e-${property.property_address.replace(/\s+/g, '-')}-${selectedYear}.html`
          mimeType = 'text/html'
          break

        case 'turbotax':
          content = generateTurboTaxFormat(scheduleEData)
          filename = `schedule-e-turbotax-${property.property_address.replace(/\s+/g, '-')}-${selectedYear}.txt`
          mimeType = 'text/plain'
          break
      }

      const blob = new Blob([content], { type: mimeType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('Export Complete', `Schedule E exported as ${format.toUpperCase()}`)
    } catch (error) {
      console.error('Export error:', error)
      showError('Export Error', `Failed to export Schedule E as ${format.toUpperCase()}`)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showSuccess('Copied!', 'Schedule E data copied to clipboard')
    } catch (error) {
      showError('Copy Failed', 'Failed to copy to clipboard')
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Schedule E Helper - {selectedYear}
                </h3>
                <p className="text-sm text-gray-600">
                  Supplemental Income and Loss from Rental Real Estate
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleExportScheduleE('csv')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                CSV
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleExportScheduleE('pdf')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                PDF
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleExportScheduleE('turbotax')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                TurboTax
              </Button>
            </div>
          </div>
        </div>

        {/* Property Information */}
        <div className="p-6 bg-gray-50">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Property Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Address:</span>
              <p className="text-gray-900">{property.property_address}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Tax Year:</span>
              <p className="text-gray-900">{selectedYear}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Property Type:</span>
              <p className="text-gray-900 capitalize">
                {property.property_type?.replace('-', ' ') || 'Rental Property'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rental Income Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Rental Income</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Rental Income (Line 3)
            </label>
            <Input
              type="number"
              value={formState.rental_income}
              onChange={(e) => setFormState(prev => ({ ...prev, rental_income: e.target.value }))}
              placeholder="0"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <Info className="w-4 h-4 inline mr-1" />
                Enter the total rental income received during {selectedYear}.
                This includes all rent payments, late fees, and other rental-related income.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Rental Expenses</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SCHEDULE_E_EXPENSE_CATEGORIES.map(category => (
            <div key={category}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {EXPENSE_CATEGORY_LABELS[category]}
                <button
                  type="button"
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  title={SCHEDULE_E_TIPS[category]}
                >
                  <Info className="w-3 h-3 inline" />
                </button>
              </label>
              <Input
                type="number"
                value={formState.expenses[category] || ''}
                onChange={(e) => updateExpense(category, e.target.value)}
                placeholder="0"
                min="0"
                step="0.01"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Other Expenses Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Other Expenses</h4>
          <Button
            variant="secondary"
            size="sm"
            onClick={addOtherExpense}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </Button>
        </div>

        {formState.other_expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calculator className="w-8 h-8 mx-auto mb-2" />
            <p>No additional expenses added</p>
            <p className="text-sm">Click "Add Expense" to include other deductible expenses</p>
          </div>
        ) : (
          <div className="space-y-3">
            {formState.other_expenses.map((expense, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Input
                    type="text"
                    value={expense.description}
                    onChange={(e) => updateOtherExpense(index, 'description', e.target.value)}
                    placeholder="Expense description"
                  />
                </div>
                <div className="w-32">
                  <Input
                    type="number"
                    value={expense.amount.toString()}
                    onChange={(e) => updateOtherExpense(index, 'amount', parseFloat(e.target.value) || 0)}
                    placeholder="Amount"
                    min="0"
                    step="0.01"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => removeOtherExpense(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Section */}
      {scheduleEData && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Schedule E Summary</h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                Total Rental Income
              </p>
              <p className="text-2xl font-bold text-blue-900">
                ${scheduleEData.rental_income.toLocaleString()}
              </p>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-medium text-red-600 uppercase tracking-wide">
                Total Expenses
              </p>
              <p className="text-2xl font-bold text-red-900">
                ${scheduleEData.total_expenses.toLocaleString()}
              </p>
            </div>

            <div className={`${scheduleEData.net_rental_income >= 0 ? 'bg-green-50' : 'bg-red-50'} rounded-lg p-4`}>
              <p className={`text-sm font-medium ${scheduleEData.net_rental_income >= 0 ? 'text-green-600' : 'text-red-600'} uppercase tracking-wide`}>
                Net Rental Income
              </p>
              <p className={`text-2xl font-bold ${scheduleEData.net_rental_income >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                ${scheduleEData.net_rental_income.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Ready to transfer to Schedule E of your tax return
            </div>
            <Button
              onClick={() => copyToClipboard(generateScheduleESummary(scheduleEData))}
              variant="secondary"
              size="sm"
              className="flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy Summary
            </Button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <CheckSquare className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-amber-800 mb-2">Schedule E Filing Instructions</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Use Form 1040 Schedule E, Part I for rental real estate income and expenses</li>
              <li>• Report each property separately if you own multiple rental properties</li>
              <li>• Keep all receipts and documentation for expenses claimed</li>
              <li>• Depreciation should be calculated using Form 4562 if this is the first year</li>
              <li>• Passive activity loss rules may limit deductions depending on your income level</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions for export formats
function generateScheduleECSV(data: ScheduleEData): string {
  const lines = [
    'Schedule E - Supplemental Income and Loss',
    `Property Address,${data.property_address}`,
    `Tax Year,${data.year}`,
    '',
    'Income',
    `Rental Income,${data.rental_income}`,
    '',
    'Expenses',
    `Advertising,${data.advertising}`,
    `Auto and Travel,${data.auto_travel}`,
    `Cleaning and Maintenance,${data.cleaning_maintenance}`,
    `Commissions,${data.commissions}`,
    `Insurance,${data.insurance}`,
    `Legal and Professional Services,${data.legal_professional}`,
    `Management Fees,${data.management_fees}`,
    `Mortgage Interest,${data.mortgage_interest}`,
    `Other Interest,${data.other_interest}`,
    `Repairs,${data.repairs}`,
    `Supplies,${data.supplies}`,
    `Taxes,${data.taxes}`,
    `Utilities,${data.utilities}`,
    `Depreciation,${data.depreciation}`,
    ...data.other_expenses.map(exp => `${exp.description},${exp.amount}`),
    '',
    `Total Expenses,${data.total_expenses}`,
    `Net Rental Income (Loss),${data.net_rental_income}`
  ]

  return lines.join('\n')
}

function generateScheduleEHTML(data: ScheduleEData): string {
  return `
    <html>
    <head>
      <title>Schedule E - ${data.property_address}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .section { margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
        .amount { text-align: right; }
        .total { font-weight: bold; border-top: 2px solid #333; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Schedule E - Supplemental Income and Loss</h1>
        <h2>Rental Real Estate</h2>
        <p><strong>Property:</strong> ${data.property_address}</p>
        <p><strong>Tax Year:</strong> ${data.year}</p>
      </div>

      <div class="section">
        <h3>Income</h3>
        <table>
          <tr><td>Rents received</td><td class="amount">$${data.rental_income.toLocaleString()}</td></tr>
        </table>
      </div>

      <div class="section">
        <h3>Expenses</h3>
        <table>
          <tr><td>Advertising</td><td class="amount">$${data.advertising.toLocaleString()}</td></tr>
          <tr><td>Auto and travel</td><td class="amount">$${data.auto_travel.toLocaleString()}</td></tr>
          <tr><td>Cleaning and maintenance</td><td class="amount">$${data.cleaning_maintenance.toLocaleString()}</td></tr>
          <tr><td>Commissions</td><td class="amount">$${data.commissions.toLocaleString()}</td></tr>
          <tr><td>Insurance</td><td class="amount">$${data.insurance.toLocaleString()}</td></tr>
          <tr><td>Legal and other professional fees</td><td class="amount">$${data.legal_professional.toLocaleString()}</td></tr>
          <tr><td>Management fees</td><td class="amount">$${data.management_fees.toLocaleString()}</td></tr>
          <tr><td>Mortgage interest paid to banks</td><td class="amount">$${data.mortgage_interest.toLocaleString()}</td></tr>
          <tr><td>Other interest</td><td class="amount">$${data.other_interest.toLocaleString()}</td></tr>
          <tr><td>Repairs</td><td class="amount">$${data.repairs.toLocaleString()}</td></tr>
          <tr><td>Supplies</td><td class="amount">$${data.supplies.toLocaleString()}</td></tr>
          <tr><td>Taxes</td><td class="amount">$${data.taxes.toLocaleString()}</td></tr>
          <tr><td>Utilities</td><td class="amount">$${data.utilities.toLocaleString()}</td></tr>
          <tr><td>Depreciation expense</td><td class="amount">$${data.depreciation.toLocaleString()}</td></tr>
          ${data.other_expenses.map(exp =>
            `<tr><td>${exp.description}</td><td class="amount">$${exp.amount.toLocaleString()}</td></tr>`
          ).join('')}
          <tr class="total"><td>Total expenses</td><td class="amount">$${data.total_expenses.toLocaleString()}</td></tr>
        </table>
      </div>

      <div class="section">
        <table>
          <tr class="total">
            <td><strong>Income less expenses (rental real estate income or loss)</strong></td>
            <td class="amount"><strong>$${data.net_rental_income.toLocaleString()}</strong></td>
          </tr>
        </table>
      </div>
    </body>
    </html>
  `
}

function generateTurboTaxFormat(data: ScheduleEData): string {
  return `TurboTax Import Format - Schedule E
Property: ${data.property_address}
Year: ${data.year}

Rental Income: ${data.rental_income}
Advertising: ${data.advertising}
Auto and Travel: ${data.auto_travel}
Cleaning and Maintenance: ${data.cleaning_maintenance}
Commissions: ${data.commissions}
Insurance: ${data.insurance}
Legal and Professional: ${data.legal_professional}
Management Fees: ${data.management_fees}
Mortgage Interest: ${data.mortgage_interest}
Other Interest: ${data.other_interest}
Repairs: ${data.repairs}
Supplies: ${data.supplies}
Taxes: ${data.taxes}
Utilities: ${data.utilities}
Depreciation: ${data.depreciation}
${data.other_expenses.map(exp => `${exp.description}: ${exp.amount}`).join('\n')}

Net Rental Income: ${data.net_rental_income}`
}

function generateScheduleESummary(data: ScheduleEData): string {
  return `Schedule E Summary for ${data.property_address} (${data.year})

Rental Income: $${data.rental_income.toLocaleString()}
Total Expenses: $${data.total_expenses.toLocaleString()}
Net Rental Income: $${data.net_rental_income.toLocaleString()}`
}